// booking-confirm.component.ts
//
// PUBLIC page — reached via the emailed link ({clientUrl}/book/confirm/:token).
// No AuthService, no JWT, no auth guard. Must be declared in a module and routed
// OUTSIDE whatever guard wraps the rest of the app — see ROUTING-PATCH.md.
//
// NOTE ON FILE PATHS: this file assumes a location alongside your other feature
// components; adjust the two relative imports below (PublicBookingService,
// environment) to match your actual folder structure — that wasn't provided,
// so these are written to be moved rather than assumed correct as-is.

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PublicBookingService, BookingSlot, BookingPageData } from '../../services/public-booking.service';
import * as moment from 'moment-timezone'; // stack already includes moment-timezone on the frontend (Vol.9)

interface DayGroup {
  dateLabel: string;   // e.g. "Monday, 18 Aug"
  slots: BookingSlot[];
}

@Component({
  selector:    'app-booking-confirm',
  templateUrl: './booking-confirm.component.html',
  styleUrls:   ['./booking-confirm.component.scss']
})
export class BookingConfirmComponent implements OnInit {

  token: string = '';

  loading:  boolean = true;
  page:     BookingPageData | null = null;
  loadError: string = '';

  clientTimezone: string = 'UTC';
  dayGroups: DayGroup[] = [];
  selectedSlot: BookingSlot | null = null;

  confirming: boolean = false;
  confirmed:  boolean = false;
  confirmResultMsg: string = '';

  declining: boolean = false;
  declined:  boolean = false;

  constructor(
    private route: ActivatedRoute,
    private bookingService: PublicBookingService
  ) {}

  async ngOnInit() {
    this.clientTimezone = this.getClientTimezone();
    this.token = this.route.snapshot.paramMap.get('token') || '';

    if (!this.token) {
      this.loadError = 'This booking link is missing a required piece. Please use the link from your email.';
      this.loading = false;
      return;
    }

    await this.loadPage();
  }

  private getClientTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'UTC';
    }
  }

  async loadPage() {
    this.loading = true;
    const result = await this.bookingService.getBookingPage(this.token);
    this.loading = false;

    if (!result || result.status === 0) {
      this.loadError = result?.msg || 'This booking link could not be loaded.';
      this.page = null;
      return;
    }

    this.page = result;
    this.groupSlotsByDay(result.slots || []);
  }

  private groupSlotsByDay(slots: BookingSlot[]) {
    const groups: { [key: string]: DayGroup } = {};

    for (const slot of slots) {
      // Display in the CLIENT's browser timezone, not the rep's — same
      // getUserTimezone()-driven display pattern already used in followup-new
      // for the rep's own calendar entries.
      const local = moment.tz(slot.start, this.clientTimezone);
      const key = local.format('YYYY-MM-DD');
      if (!groups[key]) {
        groups[key] = { dateLabel: local.format('dddd, D MMM'), slots: [] };
      }
      groups[key].slots.push(slot);
    }

    this.dayGroups = Object.keys(groups).sort().map(k => groups[k]);
  }

  formatSlotTime(slot: BookingSlot): string {
    return moment.tz(slot.start, this.clientTimezone).format('h:mm A');
  }

  selectSlot(slot: BookingSlot) {
    this.selectedSlot = slot;
  }

  async confirmBooking() {
    if (!this.selectedSlot) return;
    this.confirming = true;

    const result = await this.bookingService.confirmSlot(this.token, this.selectedSlot, this.clientTimezone);
    this.confirming = false;

    if (result?.status === 1) {
      this.confirmed = true;
      this.confirmResultMsg = result.msg || 'Meeting confirmed.';
    } else if (result?.slot_taken) {
      // Race condition — someone else booked it first. Reload slots so the
      // client can immediately pick a different one instead of hitting a dead end.
      this.confirmResultMsg = '';
      await this.loadPage();
      this.selectedSlot = null;
      this.loadError = result.msg;
    } else {
      this.loadError = result?.msg || 'Could not confirm this booking. Please try again.';
    }
  }

  async declineBooking() {
    if (!window.confirm('Let the sender know this time doesn\'t work for you?')) return;
    this.declining = true;
    const result = await this.bookingService.declineBooking(this.token);
    this.declining = false;

    if (result?.status === 1) {
      this.declined = true;
    } else {
      this.loadError = result?.msg || 'Could not send your response. Please try again.';
    }
  }
}
